<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import { insuranceTabs } from './mocks'
import { Button } from '~/components/ui/button'
import 'swiper/css/navigation'

const selectedTab = ref(insuranceTabs[0].id)
const isChanging = ref(false)
const swiperInstance = ref<SwiperType | null>(null)

const currentUrl = computed(() => {
  const tab = insuranceTabs.find((tab) => tab.id === selectedTab.value)
  return tab?.url || insuranceTabs[0].url
})

const onSwiper = (swiper: SwiperType) => {
  swiperInstance.value = swiper
}

const onTabClick = (tabId: string, index: number) => {
  selectedTab.value = tabId
  if (swiperInstance.value) {
    swiperInstance.value.slideTo(index)
  }
}

watch(selectedTab, () => {
  isChanging.value = true
  setTimeout(() => {
    isChanging.value = false
  }, 500)
})
</script>

<template>
  <Tabs v-model="selectedTab" class="w-full space-y-5">
    <header class="space-y-5 lg:flex lg:items-center lg:justify-between lg:space-y-0">
      <Heading tag="h4" class="min-w-[240px]">{{
        $t('widget.insurance_calculators.heading')
      }}</Heading>
      <div class="w-full lg:w-[calc(100%-300px)]">
        <div class="relative overflow-hidden rounded-lg">
          <Swiper
            class="insurance-tabs-swiper"
            :slides-per-view="'auto'"
            :space-between="16"
            :modules="[Navigation]"
            navigation
            @swiper="onSwiper"
          >
            <SwiperSlide v-for="(tab, index) in insuranceTabs" :key="tab.id" class="!w-auto">
              <Button
                :variant="selectedTab === tab.id ? 'blue' : 'white'"
                :class="[
                  'whitespace-nowrap transition-all duration-200',
                  selectedTab === tab.id
                    ? 'border-blue-600 bg-blue-600 text-white hover:border-blue-700 hover:bg-blue-700'
                    : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900'
                ]"
                @click="onTabClick(tab.id, index)"
              >
                {{ $t(tab.label) }}
              </Button>
            </SwiperSlide>
          </Swiper>

          <!-- Fade gradients for mobile scroll indication -->
          <div
            class="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-4 bg-gradient-to-r from-gray-100 to-transparent lg:hidden"
          />
          <div
            class="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-4 bg-gradient-to-l from-gray-100 to-transparent lg:hidden"
          />
        </div>
      </div>
    </header>

    <ContentBlock variant="white">
      <div class="overflow-hidden rounded-lg border">
        <iframe
          :src="currentUrl"
          class="min-h-[575px] rounded-lg border-0 transition-opacity duration-300 ease-in-out"
          :class="{ 'opacity-0': isChanging, 'opacity-100': !isChanging }"
          style="width: calc(100% + 2px); margin: -1px"
          loading="lazy"
        />
      </div>
    </ContentBlock>
  </Tabs>
</template>

<style scoped>
.insurance-tabs-swiper {
  --swiper-navigation-color: #2d2d2d;
  --swiper-navigation-sides-offset: -1px;
}

.insurance-tabs-swiper :deep(.swiper-button-next),
.insurance-tabs-swiper :deep(.swiper-button-prev) {
  @apply hidden h-10 w-10 rounded-lg bg-white shadow-lg translate-y-0.5 lg:flex;
}

.insurance-tabs-swiper :deep(.swiper-navigation-icon) {
  @apply h-4 w-4;
}

.insurance-tabs-swiper :deep(.swiper-button-next.swiper-button-disabled),
.insurance-tabs-swiper :deep(.swiper-button-prev.swiper-button-disabled) {
  @apply hidden;
}
</style>
